"use client";

import * as React from "react";

/**
 * Disabled-state Google + GitHub buttons for the auth pages, styled to match
 * the Account Creation Flow design (full-width, vertically stacked, with
 * brand glyphs).
 *
 * The backend's /api/auth/login already understands `idp_hint=google` and
 * `idp_hint=github`; flipping these on later means removing the `disabled`
 * prop and wiring up `onClick` to redirectToLogin({ idpHint }).
 */
export function FederatedAuthRow({
  disabledReason = "Coming soon",
  onGoogleClick,
  onGithubClick,
}: {
  readonly disabledReason?: string;
  readonly onGoogleClick?: () => void;
  readonly onGithubClick?: () => void;
}) {
  const googleDisabled = !onGoogleClick;
  const githubDisabled = !onGithubClick;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <SSOButton
        label="Continue with Google"
        glyph={<GoogleGlyph size={18} />}
        onClick={onGoogleClick}
        disabled={googleDisabled}
        title={googleDisabled ? disabledReason : undefined}
      />
      {/* <SSOButton
        label="Continue with GitHub"
        glyph={<GithubGlyph size={18} />}
        onClick={onGithubClick}
        disabled={githubDisabled}
        title={githubDisabled ? disabledReason : undefined}
      /> */}
    </div>
  );
}

function SSOButton({
  label,
  glyph,
  onClick,
  disabled,
  title,
}: {
  readonly label: string;
  readonly glyph: React.ReactNode;
  readonly onClick?: () => void;
  readonly disabled?: boolean;
  readonly title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        height: 42,
        borderRadius: 8,
        background: "var(--auth-surface)",
        color: "var(--auth-text)",
        border: "1px solid var(--auth-border-strong)",
        fontSize: 14,
        fontWeight: 500,
        letterSpacing: "-0.005em",
        boxShadow: "var(--auth-shadow-sm)",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        transition: "background 0.12s",
        width: "100%",
      }}
      onMouseEnter={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = "var(--auth-bg-soft)";
      }}
      onMouseLeave={(e) => {
        if (disabled) return;
        e.currentTarget.style.background = "var(--auth-surface)";
      }}
    >
      {glyph}
      <span>{label}</span>
    </button>
  );
}

function GoogleGlyph({ size = 18 }: { readonly size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l.003-.002 6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

function GithubGlyph({ size = 18 }: { readonly size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 .5C5.73.5.5 5.73.5 12c0 5.08 3.29 9.39 7.86 10.91.58.1.79-.25.79-.56 0-.28-.01-1.02-.02-2-3.2.69-3.88-1.54-3.88-1.54-.52-1.32-1.27-1.67-1.27-1.67-1.04-.71.08-.7.08-.7 1.15.08 1.76 1.18 1.76 1.18 1.02 1.75 2.68 1.25 3.34.96.1-.74.4-1.25.73-1.54-2.55-.29-5.24-1.28-5.24-5.69 0-1.26.45-2.29 1.18-3.1-.12-.29-.51-1.46.11-3.05 0 0 .97-.31 3.18 1.18a11 11 0 0 1 5.78 0c2.21-1.49 3.18-1.18 3.18-1.18.62 1.59.23 2.76.11 3.05.74.81 1.18 1.84 1.18 3.1 0 4.43-2.69 5.4-5.26 5.68.41.36.78 1.06.78 2.13 0 1.54-.01 2.78-.01 3.16 0 .31.21.67.8.55 4.56-1.52 7.85-5.83 7.85-10.91C23.5 5.73 18.27.5 12 .5z"
      />
    </svg>
  );
}
